import { Drawer, Tag } from '@billy/design-system'
import { useTranslation } from 'react-i18next'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { invoiceStatusToTagColor } from '@features/invoicesLegacy/constants/invoiceStatusToTagColor'
import { invoiceStatusToTitleKey } from '@features/invoicesLegacy/constants/invoiceStatusToTitleKey'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { getInvoiceStatus } from '@features/invoicesLegacy/utils/getInvoiceStatus'
import { InvoiceType } from '@features/reconciliation/types/invoiceType'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'

import { useTransactionStaticActions } from '../../../../hooks/useTransactionStaticActions'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { AttachmentCard } from '../../elements/AttachmentsSection'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DescriptionSection } from '../../elements/DescriptionSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { DrawerSection } from '../../elements/DrawerSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'
import { TransactionDrawerSkeleton } from '../../elements/TransactionDrawerSkeleton'

interface InvoiceViewProps {
  invoice: Invoice
}

export const InvoiceView = ({ invoice }: InvoiceViewProps) => {
  const { t } = useTranslation()
  const { isVoidedEntry, transactionId, transactionNo } = useTransactionDrawerContext()

  const {
    amount,
    attachments,
    balance,
    balanceModifiers,
    contactId,
    createdTime,
    currencyId,
    dueDate,
    entryDate,
    grossAmount,
    id,
    invoiceNo,
    isPaid,
    referredCreditnoteId,
    state,
    tax,
    type,
  } = invoice
  const { contact, isLoading } = useFetchContactById(contactId)
  const { handleSelect, items, modals } = useTransactionStaticActions({
    isVoided: isVoidedEntry,
    originatorId: id,
    originatorType: TransactionOriginatorType.Invoice,
    transactionId,
  })

  if (isLoading || !invoice) {
    return <TransactionDrawerSkeleton transactionNo={transactionNo} />
  }

  const invoiceStatus = getInvoiceStatus({
    amount,
    balance,
    dueDate: dueDate ? new Date(dueDate) : undefined,
    isPaid,
    referredCreditnoteId,
    state,
    tax,
  })

  const isCreditNote = type === InvoiceType.CreditNote

  const statusTag = isCreditNote
    ? undefined
    : {
        color: invoiceStatusToTagColor[invoiceStatus],
        label: t(invoiceStatusToTitleKey[invoiceStatus]),
      }

  const paymentDate = balanceModifiers?.[0]?.entryDate

  const additionalTag = isCreditNote ? <Tag type="subtle">{t('credit_note')}</Tag> : null

  const showAttachmentsCards = attachments && attachments.length > 0

  return (
    <>
      <Drawer.Header
        actions={items}
        closable
        onAction={handleSelect}
        title={t('ledger.drawer_title', { number: transactionNo })}
      />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection
          additionalTag={additionalTag}
          amount={grossAmount}
          contactName={contact?.name}
          currency={currencyId}
          entryDate={entryDate}
          source={t('ledger.invoice_no', { number: invoiceNo })}
          status={statusTag}
        />
        <Separator />
        <BookingsSection originatorCurrency={currencyId} />
        <Separator />
        <DescriptionSection />
        <Separator />
        <DetailsSection number={invoiceNo} createdTime={createdTime} paymentDate={paymentDate} />
        {showAttachmentsCards && (
          <>
            <Separator />
            <DrawerSection title={t('ledger.attachments')}>
              {attachments.map((attachment) => (
                <AttachmentCard key={attachment.id} attachment={attachment} />
              ))}
            </DrawerSection>
          </>
        )}
        <Separator />
      </Drawer.Body>
      {modals}
    </>
  )
}
